// Passkey-protected session keys, the browser's part (backend/vault.py, docs/PHASE3_PLAN.md section 2). The
// private key, the passkeys' PRF output and the recovery code never leave this page: the backend only ever gets
// the public key, sealed slots and, at unlock, the one session's data key.
// No imports: backend/tests/test_vault_js.py runs this file under Node against the backend's side.

const SLOT_INFO = "crewbench vault slot v1";
const RECOVERY_INFO = "crewbench vault recovery v1";
const SESSION_INFO = "crewbench session key v1";
const ZERO_SALT = new Uint8Array(32); // what HKDF uses when there's no salt (RFC 5869)
const CURVE = { name: "ECDH", namedCurve: "P-256" };
const BASE32 = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
const enc = new TextEncoder();
const subtle = () => globalThis.crypto.subtle;

export function toBytes(b64u) {
  const b64 = String(b64u).replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

export function toB64u(bytes) {
  let binary = "";
  new Uint8Array(bytes).forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function hkdf(ikm, salt, info) {
  const base = await subtle().importKey("raw", ikm, "HKDF", false, ["deriveKey"]);
  return subtle().deriveKey({ name: "HKDF", hash: "SHA-256", salt, info: enc.encode(info) }, base,
    { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

async function seal(key, plaintext, aad) {
  const nonce = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const ct = await subtle().encrypt({ name: "AES-GCM", iv: nonce, additionalData: enc.encode(aad) }, key, plaintext);
  return { nonce: toB64u(nonce), ct: toB64u(new Uint8Array(ct)) };
}

async function open(key, sealed, aad) {
  const plain = await subtle().decrypt({ name: "AES-GCM", iv: toBytes(sealed.nonce), additionalData: enc.encode(aad) },
    key, toBytes(sealed.ct));
  return new Uint8Array(plain);
}

// The person's key pair: the private key as PKCS#8 bytes (only ever kept sealed), the public key raw, base64url.
export async function newKeyPair() {
  const pair = await subtle().generateKey(CURVE, true, ["deriveBits"]);
  return {
    privateKey: new Uint8Array(await subtle().exportKey("pkcs8", pair.privateKey)),
    publicKey: toB64u(new Uint8Array(await subtle().exportKey("raw", pair.publicKey))),
  };
}

// A passkey's slot: the private key sealed under its PRF output.
export async function sealForPasskey(privateKey, prf, userId, credentialId) {
  return seal(await hkdf(prf, ZERO_SALT, SLOT_INFO), privateKey, `${userId}:${credentialId}`);
}

export async function openWithPasskey(slot, prf, userId, credentialId) {
  return open(await hkdf(prf, ZERO_SALT, SLOT_INFO), slot, `${userId}:${credentialId}`);
}

// The recovery code: 20 random bytes, written as 32 base32 characters in groups of four.
export function newRecoveryCode() {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(20));
  let bits = 0, value = 0, text = "";
  bytes.forEach((b) => {
    value = (value << 8) | b; bits += 8;
    while (bits >= 5) { text += BASE32[(value >>> (bits - 5)) & 31]; bits -= 5; }
  });
  return { bytes, code: text.match(/.{4}/g).join("-") };
}

// What someone typed back: spaces, dashes and case don't matter, nor 0/O, 1/I and 8/B mix-ups. Null when it
// can't be a code.
export function parseRecoveryCode(typed) {
  const text = String(typed || "").toUpperCase().replace(/0/g, "O").replace(/1/g, "I").replace(/8/g, "B")
    .replace(/[^A-Z2-7]/g, "");
  if (text.length !== 32) return null;
  const bytes = new Uint8Array(20);
  let bits = 0, value = 0, i = 0;
  for (const c of text) {
    value = (value << 5) | BASE32.indexOf(c); bits += 5;
    if (bits >= 8) { bytes[i++] = (value >>> (bits - 8)) & 255; bits -= 8; }
  }
  return bytes;
}

export async function sealForRecovery(privateKey, codeBytes, userId) {
  return seal(await hkdf(codeBytes, ZERO_SALT, RECOVERY_INFO), privateKey, `${userId}:recovery`);
}

export async function openWithRecovery(recovery, codeBytes, userId) {
  return open(await hkdf(codeBytes, ZERO_SALT, RECOVERY_INFO), recovery, `${userId}:recovery`);
}

// A session's data key, from what the backend wrapped to the public key (vault.wrap_to): ECDH with its ephemeral
// key, HKDF-SHA256 salted with that key, AES-256-GCM with the session id as additional data.
export async function unwrapSessionKey(privateKey, wrapped, sessionId) {
  const mine = await subtle().importKey("pkcs8", privateKey, CURVE, false, ["deriveBits"]);
  const ephemeral = toBytes(wrapped.eph_pub);
  const theirs = await subtle().importKey("raw", ephemeral, CURVE, false, []);
  const shared = new Uint8Array(await subtle().deriveBits({ name: "ECDH", public: theirs }, mine, 256));
  return open(await hkdf(shared, ephemeral, SESSION_INFO), wrapped, sessionId);
}

// At unlock: the session's data key (base64url) for the backend, opened with the passkey's slot. Null for a
// session the backend still holds under MASTER_KEY (it rewraps it at this unlock).
export async function sessionKeyForUnlock(material, credentialId, prf, userId, sessionId) {
  if (material.session_key.kind !== "vault") return null;
  const privateKey = await openWithPasskey(material.slots[credentialId], prf, userId, credentialId);
  return toB64u(await unwrapSessionKey(privateKey, material.session_key, sessionId));
}
